import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faArrowRightFromBracket } from '@fortawesome/pro-light-svg-icons';

import { signOut } from 'shared/auth';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import { navigationOptions } from 'shared/account/pages/MoreOptions';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

// Super-admin pages that live in the header tabs (or are otherwise not part of
// the account dropdown). NPO/donor dropdowns use their shared `secondary` list
// directly, so this filtering only applies to the admin grid of options.
const ADMIN_TOP_TAB_TITLES = new Set(['Donations']);

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');

const MenuButton = styled(Button)`
  && {
    gap: ${SPACERS.tiny};
    padding: ${SPACERS.extraTiny} ${SPACERS.tiny};
    border-radius: 999px;
    color: ${COLORS.blackBase};
    text-transform: none;
    min-width: 0;
    &:hover,
    &[aria-expanded='true'] {
      background-color: ${COLORS.greyLight};
    }
    &:active {
      background-color: ${COLORS.greyBase};
    }
    &.Mui-focusVisible {
      outline: 2px solid ${COLORS.ctaPrimary};
      outline-offset: 2px;
    }
    .MuiSvgIcon-root {
      color: ${COLORS.greyDark};
      transition: transform 150ms ease;
    }
    &[aria-expanded='true'] .MuiSvgIcon-root {
      transform: rotate(180deg);
    }
  }
`;

const UserAvatar = styled(Avatar)`
  && {
    width: 32px;
    height: 32px;
    font-size: 13px;
    font-weight: 700;
    color: ${COLORS.whiteBase};
    background-color: ${COLORS.greyDarkest};
  }
`;

const UserName = styled(Typography)`
  && {
    font-weight: 600;
    white-space: nowrap;
    @media (max-width: 600px) {
      display: none;
    }
  }
`;

const StyledMenu = styled(Menu)`
  .MuiPaper-root {
    min-width: 220px;
    margin-top: ${SPACERS.tiny};
    border: 1px solid ${COLORS.greyLight};
    box-shadow: 0 8px 24px rgba(55, 61, 63, 0.12);
  }
  .MuiMenuItem-root {
    font-size: 14px;
    gap: ${SPACERS.tiny};
    &.Mui-selected {
      color: ${COLORS.ctaPrimary};
      background-color: ${COLORS.ctaPrimaryBackgroundLighter};
    }
  }
  .MuiListItemIcon-root {
    min-width: 24px;
    color: ${COLORS.greyDark};
  }
`;

export default function AccountMenu() {
  const { push } = useHistory();
  const { pathname } = useLocation();
  const { name, isAdmin, isNPO, user } = useUserActions();
  const reportVisibility = useFeatureFlags('reportVisibility');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const options = useMemo(() => {
    if (isAdmin) {
      return navigationOptions.admin.secondary.filter(
        option => !option.showOnTop && !ADMIN_TOP_TAB_TITLES.has(option.title),
      );
    }
    if (isNPO) {
      return reportVisibility
        ? navigationOptions.npo.secondaryWithReportSharing
        : navigationOptions.npo.secondary;
    }
    return navigationOptions.donor.secondary;
  }, [isAdmin, isNPO, reportVisibility]);

  const close = () => setAnchorEl(null);
  const navigate = (path: string) => {
    close();
    push(path);
  };
  const logout = () => {
    close();
    signOut();
    push('/login');
  };

  const userLogo = user?.attributes?.userLogo || undefined;
  const initials = initialsOf(name);

  return (
    <>
      <MenuButton
        id="account-menu-button"
        aria-label={`Account menu for ${name}`}
        aria-haspopup="menu"
        aria-controls={open ? 'account-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        disableElevation
        onClick={e => setAnchorEl(e.currentTarget)}
      >
        <UserAvatar src={userLogo} alt={name}>
          {initials}
        </UserAvatar>
        <UserName variant="body2">{name}</UserName>
        <KeyboardArrowDownIcon />
      </MenuButton>
      <StyledMenu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        MenuListProps={{ 'aria-labelledby': 'account-menu-button' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {options.map(({ path, title, icon }) => (
          <MenuItem
            key={path}
            selected={pathname.startsWith(path)}
            onClick={() => navigate(path)}
          >
            {icon && (
              <ListItemIcon>
                <FontAwesomeIcon icon={icon as IconProp} />
              </ListItemIcon>
            )}
            <ListItemText>{title}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={logout}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faArrowRightFromBracket as IconProp} />
          </ListItemIcon>
          <ListItemText>Log Out</ListItemText>
        </MenuItem>
      </StyledMenu>
    </>
  );
}
